import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { Subscription, noop } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { STORAGE_KEY } from '../../../common/enum/storage-key.enum';
import { RR_BATCH_TYPE } from '../../../common/enum/table/batch-type.enum';
import { RR_TABLE_ACTION_TYPE } from '../../../common/enum/table/table-action-type.enum';
import { RRIBaseModel } from '../../../common/interfaces/base-model.interface';
import { RRIPagination } from '../../../common/interfaces/base-pagination.interface';
import { RRIQueryParams } from '../../../common/interfaces/base-query-params.interface';
import { RRIBaseService } from '../../../common/interfaces/base-service.interface';
import { RRISort } from '../../../common/interfaces/base-sort.interface';
import { RRIFileService } from '../../../common/interfaces/file-service.interface';
import { RRFilterContainerModel } from '../../../common/interfaces/filters/filter-container.interface';
import { RRIFilterOutput } from '../../../common/interfaces/filters/filter-output.interface';
import { RRIFilter } from '../../../common/interfaces/filters/filter.interface';
import { RRIStorageService } from '../../../common/interfaces/storage-service.interface';
import {
  RRIBatchOption,
  RRIDownloadBatchOptionSettings,
} from '../../../common/interfaces/table/batch-options.interface';
import { RRITableActionOutput } from '../../../common/interfaces/table/table-action-output.interface';
import { RRITableDeleteActionSettings } from '../../../common/interfaces/table/table-delete-action-settings.interface';
import { RRITableRowClick } from '../../../common/interfaces/table/table-row-click.interface';
import { RRTableContainerModel } from '../../../common/models/table/table-container.model';
import { RRBatchOption } from '../../../common/types/batch-option.type';
import { RRFilterBy } from '../../../common/types/filter-by-value.type';
import { RRFilterOption } from '../../../common/types/filter-option.type';
import { RRTableAction } from '../../../common/types/table-action.type';
import { RR_PROGRESSBAR_TYPE } from '../../../progressbar/models/progressbar-type.enum';
import { RRProgressbarModel } from '../../../progressbar/models/progressbar.model';
import { RRSpinnerModel } from '../../../spinner/models/spinner.model';

@Component({
  selector: 'rr-filtered-table',
  templateUrl: './filtered-table.component.html',
  styleUrls: ['./filtered-table.component.scss'],
})
export class FilteredTableComponent<T extends RRIBaseModel, U, R>
  implements OnInit, OnChanges, OnDestroy
{
  public spinnerStatus: RRSpinnerModel;
  public queryParams: RRIQueryParams<T, U>;
  public progressBarModel: RRProgressbarModel;
  public showProgressBar: boolean;

  private deleteAction: (row: T) => void;

  @Input() name: string;
  @Input() selectedRows: T[];
  @Input() tableContainerSettings: RRTableContainerModel<T, U>;
  @Input() filterContainerSettings: RRFilterContainerModel<any>;
  @Input() service: RRIBaseService<T, U>;
  @Input() storageService: RRIStorageService;
  @Input() fileService: RRIFileService<T>;
  @Input() notifyChanges: boolean;
  @Input() refreshTable: boolean;
  @Input() initClearCache: boolean;
  @Input() doNotFireSelectedRowsOnGetData: boolean;
  @Input() selectedRadioEntity: T;
  @Input() loadItemsWithResults: boolean;
  @Input() clearRadioSelection: boolean;
  @Input() addButtonLeft: TemplateRef<any>;
  @Input() addButtonRight: TemplateRef<any>;

  @Output() paginationChange: EventEmitter<RRIPagination> = new EventEmitter();
  @Output() sortBy: EventEmitter<RRISort<T>> = new EventEmitter();
  @Output() actionClicked: EventEmitter<RRITableActionOutput<T>> =
    new EventEmitter();
  @Output() actionConfirmed: EventEmitter<RRITableActionOutput<T>> =
    new EventEmitter();
  @Output() batchOptionClicked: EventEmitter<RRBatchOption<T, R>> =
    new EventEmitter();
  @Output() batchOptionConfirmed: EventEmitter<RRBatchOption<T, R>> =
    new EventEmitter();
  @Output() rowSelected: EventEmitter<T[]> = new EventEmitter();
  @Output() radioSelected: EventEmitter<T> = new EventEmitter();
  @Output() rowClicked: EventEmitter<RRITableRowClick<T>> = new EventEmitter();
  @Output() changedFilter: EventEmitter<RRIFilterOutput<U>[]> =
    new EventEmitter();
  @Output() dropdownFilterClicked: EventEmitter<RRIFilterOutput<U>> =
    new EventEmitter();
  // TODO
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() submit: EventEmitter<boolean> = new EventEmitter();

  @Output() changeVisableColumns: EventEmitter<(keyof T)[]> =
    new EventEmitter();

  @Output() tableWillBeRefreshed: EventEmitter<void> = new EventEmitter();
  @Output() tableRefreshed: EventEmitter<void> = new EventEmitter();
  @Output() radioSelectionCleared: EventEmitter<void> = new EventEmitter();
  @Output() selectAllClicked: EventEmitter<void> = new EventEmitter();

  private loadingDataObservable: Subscription;

  constructor() {
    this.showProgressBar = false;
    this.spinnerStatus = {
      show: false,
      text: 'Loading...',
    };
    this.progressBarModel = {
      type: RR_PROGRESSBAR_TYPE.INFO,
      animate: true,
      value: 0,
      max: 0,
      extraInfo: '',
    };

    this.queryParams = {
      sort: null,
      filters: null,
      pagination: null,
    };

    if (!this.selectedRows) {
      this.selectedRows = [];
    }

    this.loadingDataObservable = new Subscription();
  }

  ngOnInit(): void {
    if (this.storageService) {
      this.setFilterValues();
      this.setPagination();
      this.setVisibleColumns();
    }

    this.prepareQueryParameter();

    if (this.service) {
      const deleteAction = this.getDeleteAction();
      this.queryParams.includeResult = this.loadItemsWithResults;
      let forceRefresh: boolean = false;
      if (this.initClearCache) {
        forceRefresh = true;
      }
      this.getTableData(forceRefresh);

      if (deleteAction) {
        this.deleteAction = deleteAction.delete;
      }
    }

    if (this.fileService) {
      this.fileService.isLoading.subscribe((data) => {
        this.showProgressBar = data.show;

        this.progressBarModel = {
          type: RR_PROGRESSBAR_TYPE.INFO,
          animate: true,
          value: data.progressBarValue,
          max: data.maxEntities,
          extraInfo: this.getProgressBarInfo(
            data.progressBarValue,
            data.maxEntities,
          ),
        };
      });
    }
  }

  ngOnChanges(): void {
    if (this.refreshTable) {
      this.prepareQueryParameter();

      if (this.service) {
        this.getTableData(true);
      }

      if (this.fileService) {
        this.fileService.isLoading.subscribe((data) => {
          this.showProgressBar = data.show;

          this.progressBarModel = {
            type: RR_PROGRESSBAR_TYPE.INFO,
            animate: true,
            value: data.progressBarValue,
            max: data.maxEntities,
            extraInfo: this.getProgressBarInfo(
              data.progressBarValue,
              data.maxEntities,
            ),
          };
        });
      }
    }
  }

  ngOnDestroy() {
    this.loadingDataObservable.unsubscribe();
  }

  public onFiltersChange(event: RRIFilterOutput<any>[]): void {
    const newFilterSettigns = this.filterContainerSettings;
    newFilterSettigns.filters.forEach(
      (filter: RRIFilter<RRFilterBy<any>, RRFilterOption<any>>) => {
        const newFilter = event.find(
          (f: RRIFilterOutput<U>) =>
            f.filterBy.fieldName === filter.filterBy.fieldName,
        );

        if (newFilter) {
          filter.filterBy.value = newFilter.filterBy.value;
        }
      },
    );
    this.filterContainerSettings = Object.assign({}, newFilterSettigns);
    if (this.service) {
      if (!this.checkIfValueChanged(this.queryParams.filters, event)) {
        return;
      }
      this.queryParams.filters = event;
      this.tableContainerSettings.tablePagination.settings.currentPage = 1;
      this.queryParams.pagination.currentPage = 1;

      if (this.storageService) {
        this.storageService.setItem(
          STORAGE_KEY.PAGINATION,
          this.queryParams.pagination,
        );
      }

      if (this.notifyChanges && this.paginationChange) {
        this.paginationChange.emit(this.queryParams.pagination);
      }
      this.getTableData();
    }

    if (this.storageService) {
      this.storageService.setItem(STORAGE_KEY.FILTERS, event);
    }

    if (this.notifyChanges && this.changedFilter) {
      this.changedFilter.emit(event);
    }
  }

  public onFiltersSubmit(event: boolean): void {
    if (this.service) {
      this.getTableData();
    }

    if (this.notifyChanges && this.submit) {
      this.submit.emit(event);
    }
  }

  public onPaginationChange(event: RRIPagination): void {
    if (this.service) {
      this.queryParams.pagination = {
        ...event,
      };
      this.getTableData();
    }

    if (this.storageService) {
      this.storageService.setItem(STORAGE_KEY.PAGINATION, event);
    }

    if (this.notifyChanges && this.paginationChange) {
      this.paginationChange.emit(event);
    }
  }

  public onSortBy(event: RRISort<T>): void {
    if (this.service) {
      this.queryParams.sort = {
        ...event,
      };
      this.getTableData();
    }

    if (this.storageService) {
      this.storageService.setItem(STORAGE_KEY.SORT_BY, event);
    }

    if (this.notifyChanges && this.sortBy) {
      this.sortBy.emit(event);
    }
  }

  public onActionClicked(event: RRITableActionOutput<T>): void {
    if (
      event.action.type === RR_TABLE_ACTION_TYPE.DOWNLOAD &&
      !event.action.settings.doNothing &&
      this.fileService
    ) {
      this.fileService.fileDownload([event.row], null, true);
    }

    if (
      event.action.type === RR_TABLE_ACTION_TYPE.REFRESH &&
      !event.action.settings.doNothing
    ) {
      this.getTableData(true);
    }

    if (this.notifyChanges && this.actionClicked) {
      this.actionClicked.emit(event);
    }
  }

  public onActionConfirmed(event: RRITableActionOutput<T>): void {
    if (this.service && !event.action.settings.doNothing) {
      if (event.action.type === RR_TABLE_ACTION_TYPE.DELETE && event.row) {
        this.deleteRow(event.row);
      }
    }
    if (event.action.type === RR_TABLE_ACTION_TYPE.DELETE && event.row) {
      if (this.deleteAction) {
        this.deleteAction(event.row);
      }
    }

    if (this.notifyChanges && this.actionConfirmed) {
      this.actionConfirmed.emit(event);
    }
  }

  public onBatchOptionClicked(event: RRBatchOption<T, any>): void {
    if (
      event.type === RR_BATCH_TYPE.DOWNLOAD &&
      this.fileService &&
      !Array.isArray(event.settings)
    ) {
      this.fileService.fileDownload(
        event.settings.selectedRows,
        event as RRIBatchOption<RRIDownloadBatchOptionSettings<T, any>>,
        false,
      );
    }

    if (this.notifyChanges && this.batchOptionClicked) {
      this.batchOptionClicked.emit(event);
    }
  }

  public onBatchOptionConfirmed(event: RRBatchOption<T, any>): void {
    if (this.service) {
      if (
        event.type === RR_BATCH_TYPE.DELETE &&
        this.service.deleteMultipleEntries &&
        !Array.isArray(event.settings)
      ) {
        this.deleteMultipleRows(event.settings.selectedRows);
      }
    }

    if (this.notifyChanges && this.batchOptionConfirmed) {
      this.batchOptionConfirmed.emit(event);
    }
  }

  public onRowSelected(event: T[]): void {
    this.selectedRows = event;

    if (this.notifyChanges && this.rowSelected) {
      this.rowSelected.emit(event);
    }
  }

  public onRowClicked(event: RRITableRowClick<T>): void {
    if (this.notifyChanges && this.rowClicked) {
      this.rowClicked.emit(event);
    }
  }

  public onChangeVisableColumns(event: (keyof T)[]) {
    if (this.storageService) {
      this.storageService.setItem(STORAGE_KEY.VISIBLE_COLUMNS, event);
    }

    if (this.notifyChanges && this.changeVisableColumns) {
      this.changeVisableColumns.emit(event);
    }
  }

  public onDropFilterClicked(event: RRIFilterOutput<any>) {
    if (this.notifyChanges && this.dropdownFilterClicked) {
      this.dropdownFilterClicked.emit(event);
    }
  }

  public onRadioSelected(event: T) {
    this.radioSelected.emit(event);
  }

  public onRadioSelectionCleared() {
    this.radioSelectionCleared.emit();
  }

  public onSelectAllClicked(): void {
    this.selectAllClicked.emit();
  }

  private checkIfValueChanged(
    filters: RRIFilterOutput<any>[],
    event: RRIFilterOutput<any>[],
  ): boolean {
    for (const filter of filters) {
      const eventItem = event.find((o) => {
        o.filterBy.fieldName === filter.filterBy.fieldName;
      });
      if (!eventItem) {
        return true;
      }
      if (filter.filterBy.value !== eventItem.filterBy.value) {
        return true;
      }
    }
    return false;
  }

  private getTableData(forceReload?: boolean): void {
    if (this.service) {
      this.showSpinner();
      this.tableWillBeRefreshed.emit();

      this.loadingDataObservable.unsubscribe();
      this.loadingDataObservable = this.service
        .getData(this.queryParams, forceReload)
        .pipe(
          finalize(() => {
            this.hideSpinner();
            this.tableRefreshed.emit();
          }),
        )
        .subscribe(
          (response) => {
            if (this.tableContainerSettings) {
              this.tableContainerSettings.table.results = response.results;
              this.tableContainerSettings.tablePagination = {
                ...this.tableContainerSettings.tablePagination,
                settings: {
                  ...this.tableContainerSettings.tablePagination.settings,
                  ...response.pagination,
                },
              };
            }
            if (!this.doNotFireSelectedRowsOnGetData) {
              this.onRowSelected([]);
              this.onRadioSelected(null);
            }
          },
          (error) => {
            console.log('Filtered Table', error);
            this.tableContainerSettings.table.results = [];
          },
        );
    }
  }

  private deleteRow(row: T): void {
    this.showSpinner();

    this.service.deleteEntry(row).subscribe(
      () => {
        this.getTableData(true);
      },
      () => {
        //Errors should be handled in each service to have a more descriptive error message
        noop();
      },
      () => {
        this.hideSpinner();
      },
    );
  }

  private deleteMultipleRows(rows: T[]): void {
    this.showSpinner();

    this.service
      .deleteMultipleEntries(rows)
      .pipe(
        finalize(() => {
          this.getTableData(true);
        }),
      )
      .subscribe(
        () => {},
        () => {
          //Errors should be handled in each service to have a more descriptive error message
          noop();
        },
        () => {
          this.hideSpinner();
          this.onRowSelected([]);
        },
      );
  }

  private getDeleteAction(): RRITableDeleteActionSettings<T> {
    if (
      !this.tableContainerSettings ||
      !this.tableContainerSettings.table ||
      !this.tableContainerSettings.table.tableActions ||
      !this.tableContainerSettings.table.tableActions.length
    ) {
      return null;
    }

    const action = this.tableContainerSettings.table.tableActions.find(
      (action: RRTableAction<T>) => action.type === RR_TABLE_ACTION_TYPE.DELETE,
    );

    return action ? (action.settings as RRITableDeleteActionSettings<T>) : null;
  }

  private mapFilterToFilterOutput(
    filters: RRIFilter<RRFilterBy<any>, RRFilterOption<any>>[],
  ): RRIFilterOutput<any>[] {
    return filters.map((filter) => {
      return {
        type: filter.type,
        filterBy: filter.filterBy,
      };
    });
  }
  private showSpinner(): void {
    this.spinnerStatus.show = true;
  }

  private hideSpinner(): void {
    this.spinnerStatus.show = false;
  }

  private getProgressBarInfo(value: number, max: number): string {
    if (value && max && !Number.isNaN(value) && !Number.isNaN(max)) {
      return `${value} out of ${max}`;
    }

    return 'Loading...';
  }

  private prepareQueryParameter() {
    this.queryParams = {
      sort:
        this.tableContainerSettings && this.tableContainerSettings.table
          ? this.tableContainerSettings.table.sortBy
          : null,
      filters: this.filterContainerSettings
        ? this.mapFilterToFilterOutput(this.filterContainerSettings.filters)
        : null,
      pagination:
        this.tableContainerSettings &&
        this.tableContainerSettings.tablePagination
          ? this.tableContainerSettings.tablePagination.settings
          : null,
    };
  }

  private setVisibleColumns(): void {
    const visibleColumns = this.storageService.getItem(
      STORAGE_KEY.VISIBLE_COLUMNS,
      true,
    );

    if (!visibleColumns) {
      return;
    }
    this.tableContainerSettings.table.visibleColumns = visibleColumns;
  }

  private setPagination(): void {
    const pagination: RRIPagination = this.storageService.getItem(
      STORAGE_KEY.PAGINATION,
      true,
    );
    if (pagination) {
      this.tableContainerSettings.tablePagination.settings = {
        ...this.tableContainerSettings.tablePagination.settings,
        pageSize: pagination.pageSize
          ? pagination.pageSize
          : this.tableContainerSettings.tablePagination.settings.pageSize,
      };
    }
  }

  private setFilterValues() {
    const storedFilters: RRIFilter<RRFilterBy<any>, any>[] =
      this.storageService.getItem(STORAGE_KEY.FILTERS, true);
    if (!storedFilters) {
      return;
    }

    for (const filter of storedFilters) {
      const filterItem = this.filterContainerSettings.filters.find(
        (o) => o.filterBy.fieldName === filter.filterBy.fieldName,
      );
      if (filterItem && filterItem.filterBy) {
        filterItem.filterBy.value = filter.filterBy.value;
      }
    }
  }
}
